import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../utils/apiClient';
import { handleAuthError } from '../utils/authErrorHandler';
import './EntityJoinModal.css';

const EntityJoinModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1); // 1: choix type, 2: formulaire
  const [requestType, setRequestType] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form states
  const [formData, setFormData] = useState({
    organizationId: '',
    organizationName: '',
    organizationDescription: '',
    requestedRole: 'user'
  });

  useEffect(() => {
    if (isOpen && requestType === 'join_existing') {
      loadOrganizations();
    }
  }, [isOpen, requestType]);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/entity-join/organizations');
      if (response.data.success) {
        setOrganizations(response.data.organizations);
      }
    } catch (error) {
      if (handleAuthError(error)) return;
      setError(t('entityJoinModal.errors.loadOrganizations'));
    } finally {
      setLoading(false);
    }
  };

  const handleTypeSelection = (type) => {
    setRequestType(type);
    setStep(2);
    setError('');
    setSuccess('');
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const requestData = {
        requestType,
        ...formData
      };

      const response = await apiClient.post('/api/entity-join/request', requestData);
      
      if (response.data.success) {
        setSuccess(t('entityJoinModal.successMessage'));
        setTimeout(() => {
          onSuccess && onSuccess();
          handleClose();
        }, 2000);
      }

    } catch (error) {
      if (handleAuthError(error)) return;
      setError(error.response?.data?.error || t('entityJoinModal.errors.sendRequest'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setRequestType('');
    setFormData({
      organizationId: '',
      organizationName: '',
      organizationDescription: '',
      requestedRole: 'user'
    });
    setError('');
    setSuccess('');
    onClose();
  };

  const handleBack = () => {
    setStep(1);
    setRequestType('');
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="entity-join-modal-overlay">
      <div className="entity-join-modal">
        <div className="modal-header">
          <h2>
            {step === 1 && t('entityJoinModal.titles.main')}
            {step === 2 && requestType === 'join_existing' && t('entityJoinModal.titles.joinExisting')}
            {step === 2 && requestType === 'create_organization' && t('entityJoinModal.titles.createOrganization')}
          </h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        <div className="modal-content">
          {step === 1 && (
            <div className="type-selection">
              <p>{t('entityJoinModal.question')}</p>
              
              <div className="option-cards">
                <div 
                  className="option-card"
                  onClick={() => handleTypeSelection('join_existing')}
                >
                  <div className="option-icon">👥</div>
                  <h3>{t('entityJoinModal.options.join.title')}</h3>
                  <p>{t('entityJoinModal.options.join.description')}</p>
                  <small>{t('entityJoinModal.options.join.warning')}</small>
                </div>

                <div 
                  className="option-card"
                  onClick={() => handleTypeSelection('create_organization')}
                >
                  <div className="option-icon">🏗️</div>
                  <h3>{t('entityJoinModal.options.create.title')}</h3>
                  <p>{t('entityJoinModal.options.create.description')}</p>
                  <small>{t('entityJoinModal.options.create.warning')}</small>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <form onSubmit={handleSubmit} className="join-form">
              {requestType === 'join_existing' && (
                <>
                  <div className="form-group">
                    <label htmlFor="organizationId">{t('entityJoinModal.form.organizationToJoin')} *</label>
                    <select
                      id="organizationId"
                      name="organizationId"
                      value={formData.organizationId}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">{t('entityJoinModal.form.selectOrganization')}</option>
                      {organizations.map(org => (
                        <option key={org._id} value={org._id}>
                          {org.name} {org.description && `- ${org.description}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="requestedRole">{t('entityJoinModal.form.requestedRole')} *</label>
                    <select
                      id="requestedRole"
                      name="requestedRole"
                      value={formData.requestedRole}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="user">{t('entityJoinModal.form.roles.user')}</option>
                      <option value="org-admin">{t('entityJoinModal.form.roles.orgAdmin')}</option>
                    </select>
                    <small>
                      {formData.requestedRole === 'org-admin' && 
                        t('entityJoinModal.form.adminValidationNote')}
                    </small>
                  </div>
                </>
              )}

              {requestType === 'create_organization' && (
                <>
                  <div className="form-group">
                    <label htmlFor="organizationName">{t('entityJoinModal.form.organizationName')} *</label>
                    <input
                      type="text"
                      id="organizationName"
                      name="organizationName"
                      value={formData.organizationName}
                      onChange={handleInputChange}
                      placeholder={t('entityJoinModal.form.organizationNamePlaceholder')}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="organizationDescription">{t('entityJoinModal.form.description')}</label>
                    <textarea
                      id="organizationDescription"
                      name="organizationDescription"
                      value={formData.organizationDescription}
                      onChange={handleInputChange}
                      placeholder={t('entityJoinModal.form.descriptionPlaceholder')}
                      rows="3"
                    />
                  </div>

                  <div className="info-box">
                    <p><strong>{t('entityJoinModal.form.infoTitle')}</strong></p>
                    <p>{t('entityJoinModal.form.infoMessage')}</p>
                  </div>
                </>
              )}

              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={handleBack}>
                  ← {t('entityJoinModal.buttons.back')}
                </button>
                <button 
                  type="submit" 
                  className="btn-primary" 
                  disabled={loading}
                >
                  {loading ? t('entityJoinModal.buttons.sending') : t('entityJoinModal.buttons.sendRequest')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default EntityJoinModal;
